import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiClipboard, FiRepeat, FiCopy, FiCheck, FiExternalLink } from 'react-icons/fi';
import { getWebhookCallDetail, type WebhookCallFullDetail } from '../../services/webhookAnalyticsService';
import { isRoutineCall, webhookCallLink, webhookCallOpenLabel, webhookCallUnavailableReason } from './webhookCallLinks';
import '../../styles/Webhooks.css';

const STATUS_STYLE: Record<'done' | 'failed' | 'running', { label: string; color: string; bg: string; border: string }> = {
    done: { label: 'Done', color: '#22c55e', bg: 'rgba(34,197,94,0.12)', border: 'rgba(34,197,94,0.3)' },
    failed: { label: 'Failed', color: '#ef4444', bg: 'rgba(239,68,68,0.12)', border: 'rgba(239,68,68,0.3)' },
    running: { label: 'Running', color: '#f59e0b', bg: 'rgba(245,158,11,0.12)', border: 'rgba(245,158,11,0.3)' },
};

function statusKey(status: string): 'done' | 'failed' | 'running' {
    return status === 'DONE' ? 'done' : status === 'FAILED' ? 'failed' : 'running';
}

function formatDateTime(iso: string | null | undefined): string {
    if (!iso) return '—';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', second: '2-digit' });
}

/** Pretty-prints JSON text; returns it unchanged when it isn't JSON. */
function prettyJson(raw: string | null | undefined): string | null {
    if (raw == null || raw.trim() === '') return null;
    try { return JSON.stringify(JSON.parse(raw), null, 2); } catch { return raw; }
}

const CopyButton: React.FC<{ text: string; label: string }> = ({ text, label }) => {
    const [copied, setCopied] = useState(false);
    const copy = useCallback(() => {
        void navigator.clipboard?.writeText(text).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        });
    }, [text]);
    return (
        <button type="button" className="wh-call__copy" onClick={copy} title={copied ? 'Copied' : `Copy ${label}`} aria-label={`Copy ${label}`}>
            {copied ? <FiCheck size={12} /> : <FiCopy size={12} />}
        </button>
    );
};

const Section: React.FC<{ title: string; copyText?: string | null; children: React.ReactNode }> = ({ title, copyText, children }) => (
    <section className="wh-call__section">
        <div className="wh-call__section-head">
            <span className="wh-call__section-title">{title}</span>
            {copyText ? <CopyButton text={copyText} label={title.toLowerCase()} /> : null}
        </div>
        {children}
    </section>
);

interface WebhookCallDetailSidebarProps {
    callId: number;
    /** Back to the Webhook Analytics panel (clears the selected call). */
    onBack: () => void;
}

/**
 * Right-sidebar detail for one webhook call, opened from /settings/webhooks/report.
 * Shows what was received (method, URL, body), what came back (response or failure
 * reason) and the Command/Routine it created. Request headers aren't stored for
 * webhook calls, so there is no headers section.
 */
const WebhookCallDetailSidebar: React.FC<WebhookCallDetailSidebarProps> = ({ callId, onBack }) => {
    const navigate = useNavigate();
    const [detail, setDetail] = useState<WebhookCallFullDetail | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const controller = new AbortController();
        setLoading(true);
        setError(null);
        getWebhookCallDetail(callId, controller.signal)
            .then(setDetail)
            .catch((err) => {
                if (err?.name !== 'AbortError') setError(err?.message || 'Could not load this webhook call.');
            })
            .finally(() => {
                if (!controller.signal.aborted) setLoading(false);
            });
        return () => controller.abort();
    }, [callId]);

    const requestBody = useMemo(() => prettyJson(detail?.requestBody), [detail?.requestBody]);
    const responseBody = useMemo(() => prettyJson(detail?.responseBody), [detail?.responseBody]);

    const back = (
        <button type="button" className="wh-call__back" onClick={onBack}>
            <FiArrowLeft size={12} /> Webhook analytics
        </button>
    );

    if (loading && !detail) {
        return (
            <div className="wh-call">
                {back}
                <div className="wh-call__skeleton" aria-busy="true">
                    <div className="wh-call__skeleton-line wh-call__skeleton-line--title" />
                    <div className="wh-call__skeleton-line" />
                    <div className="wh-call__skeleton-block" />
                    <div className="wh-call__skeleton-block" />
                </div>
            </div>
        );
    }

    if (error || !detail) {
        return (
            <div className="wh-call">
                {back}
                <div className="wh-call__error">{error || 'Could not load this webhook call.'}</div>
            </div>
        );
    }

    const isRoutine = isRoutineCall(detail);
    const st = STATUS_STYLE[statusKey(detail.status)];
    const link = webhookCallLink(detail);
    const unavailable = webhookCallUnavailableReason(detail);
    const title = detail.title || detail.taskNumber || (isRoutine ? 'Routine run' : 'Command');
    const targetName = isRoutine ? (detail.routineName || detail.title || 'Routine') : (detail.taskNumber || detail.title || 'Command');

    return (
        <div className="wh-call" style={{ opacity: loading ? 0.6 : 1 }}>
            {back}

            <div className="wh-call__header">
                <div className="mcp-icon-box">{isRoutine ? <FiRepeat size={14} /> : <FiClipboard size={14} />}</div>
                <div className="wh-call__header-text">
                    <div className="wh-call__title" title={title}>{title}</div>
                    <div className="wh-call__header-meta">
                        <span>{isRoutine ? 'Routine' : 'Command'}</span>
                        <span className="wh-status-badge" style={{ color: st.color, background: st.bg, border: `1px solid ${st.border}` }}>
                            <span className="wh-status-dot" />
                            {st.label}
                        </span>
                    </div>
                </div>
            </div>

            <dl className="wh-call__meta">
                <dt>Call ID</dt><dd>#{detail.id}</dd>
                <dt>Called at</dt><dd>{formatDateTime(detail.createdAt)}</dd>
                <dt>Last updated</dt><dd>{formatDateTime(detail.modifiedAt)}</dd>
            </dl>

            <Section title="Request" copyText={detail.url}>
                <div className="wh-call__url">
                    <span className="wh-call__method">{detail.method || 'POST'}</span>
                    <code className="wh-call__url-text">{detail.url || detail.endpoint || '—'}</code>
                </div>
            </Section>

            <Section title="Body" copyText={requestBody}>
                {requestBody
                    ? <pre className="wh-call__code">{requestBody}</pre>
                    : <div className="wh-call__empty">No request body was stored.</div>}
            </Section>

            <Section title="Response" copyText={responseBody}>
                {detail.status === 'FAILED' && detail.errorMessage && (
                    <div className="wh-report__fail-badge wh-call__fail" title={`Failure Reason: ${detail.errorMessage}`}>
                        <span className="wh-report__fail-badge-label">Failure Reason:</span> {detail.errorMessage}
                    </div>
                )}
                {responseBody
                    ? <pre className="wh-call__code">{responseBody}</pre>
                    : !(detail.status === 'FAILED' && detail.errorMessage) && <div className="wh-call__empty">No response body was stored.</div>}
            </Section>

            <Section title={isRoutine ? 'Routine' : 'Command'}>
                <div className="wh-call__target">
                    <span className="wh-call__target-name" title={targetName}>{targetName}</span>
                    <button
                        type="button"
                        className="wh-call__open"
                        disabled={!link}
                        title={unavailable ?? undefined}
                        onClick={() => { if (link) navigate(link); }}
                    >
                        <FiExternalLink size={12} /> {webhookCallOpenLabel(detail)}
                    </button>
                </div>
                {unavailable && <div className="wh-call__empty">{unavailable}</div>}
            </Section>
        </div>
    );
};

export default WebhookCallDetailSidebar;
